/**
 * useSiteConfig — load the public website content config and cache it
 * module-level so every marketing page shares a single in-flight request.
 *
 * Source priority (first non-empty wins):
 *   1. Backend public endpoint `/api/parties-module/v1/public/marketing/site-config`
 *      — this is the live tenant-scoped row written by the MIS Settings →
 *      Website Content editor.  When the publish loop is healthy this is
 *      always the freshest source.
 *   2. Static `/site.config.json` (served from the React build's /public).
 *      Acts as a zero-backend fallback so the public site keeps rendering
 *      even if the API is down or the marketing module isn't migrated yet.
 *   3. Bundled `defaultConfig` — guarantees the site never crashes when
 *      both of the above fail.
 *
 * Merge strategy:
 *   - Most top-level blocks (`hero`, `features`, …) are replaced wholesale.
 *     If the payload omits the key, the default is used.
 *   - `meta` and `ui` are *deep-merged one level* so admins can override a
 *     single label (e.g. `ui.form.submitLabel`) without copying the entire
 *     scaffolding.
 */
import { useEffect, useState } from 'react';
import defaultConfig from './defaultConfig';
import { API_ORIGIN } from '../../services/apiBase';

let cachedConfig = null;
let inFlight     = null;

// Shared origin resolution (services/apiBase.js): same-origin in production,
// :9000 dev server in dev. We don't import ssApi here because the marketing
// site must remain usable when logged out (no auth header) and we want a much
// shorter timeout than the admin client's 15s.
const API_BASE = API_ORIGIN;

const PUBLIC_API_URL = `${API_BASE}/api/parties-module/v1/public/marketing/site-config`;

// Total time we'll wait for the backend before falling back to the static
// file.  Keeps the first paint snappy when the API is slow / unreachable.
const API_TIMEOUT_MS = 2500;

// Top-level blocks that should be deep-merged (one level deep) instead of
// replaced wholesale — these are infrastructure-y and users typically want
// to override one or two leaves while keeping the rest of the defaults.
const DEEP_MERGE_KEYS = new Set(['meta', 'ui']);

function deepMergeOneLevel(base, override) {
  if (!override || typeof override !== 'object') return base;
  const merged = { ...(base || {}) };
  Object.keys(override).forEach((k) => {
    const bv = (base || {})[k];
    const ov = override[k];
    if (
      bv && typeof bv === 'object' && !Array.isArray(bv) &&
      ov && typeof ov === 'object' && !Array.isArray(ov)
    ) {
      merged[k] = { ...bv, ...ov };
    } else {
      merged[k] = ov;
    }
  });
  return merged;
}

function mergeConfig(remote) {
  if (!remote || typeof remote !== 'object') return defaultConfig;
  const out = { ...defaultConfig };
  Object.keys(remote).forEach((key) => {
    if (key.startsWith('$')) return;       // skip $schema and similar metadata
    if (DEEP_MERGE_KEYS.has(key)) {
      out[key] = deepMergeOneLevel(defaultConfig[key], remote[key]);
    } else {
      out[key] = remote[key];
    }
  });
  return out;
}

async function fetchWithTimeout(url, timeoutMs) {
  // AbortController gives us a hard ceiling on how long we wait — without
  // it a slow / hung backend would block the entire marketing site.
  const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = ctrl ? setTimeout(() => ctrl.abort(), timeoutMs) : null;
  try {
    const res = await fetch(url, {
      cache: 'no-store',
      signal: ctrl ? ctrl.signal : undefined,
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  } finally {
    if (timer) clearTimeout(timer);
  }
}

async function loadFromBackend() {
  // Backend wraps the payload under a `payload` key alongside a `version`.
  // Older deploys without the marketing module return 404 — we treat that
  // as "no backend config available" and fall through to the static file.
  const json = await fetchWithTimeout(PUBLIC_API_URL, API_TIMEOUT_MS);
  if (!json || typeof json !== 'object') throw new Error('empty payload');
  if (!json.payload || typeof json.payload !== 'object') throw new Error('missing payload');
  return json.payload;
}

async function loadFromStatic() {
  // PUBLIC_URL handles sub-path deployments (e.g. /app/site.config.json)
  const base = (typeof process !== 'undefined' && process.env && process.env.PUBLIC_URL) || '';
  return await fetchWithTimeout(`${base}/site.config.json`, API_TIMEOUT_MS);
}

async function loadConfig() {
  if (cachedConfig) return cachedConfig;
  if (inFlight)     return inFlight;
  inFlight = (async () => {
    let raw = null;
    let source = 'default';
    // 1. Try the backend first — freshest data.
    try {
      raw = await loadFromBackend();
      source = 'backend';
    } catch (apiErr) {
      // 2. Fall back to the static file — zero-backend mode.
      try {
        raw = await loadFromStatic();
        source = 'static';
      } catch (fileErr) {
        // 3. Last resort — bundled defaults.  Log loudly so operators
        //    can see the publish loop is broken, but keep rendering.
        // eslint-disable-next-line no-console
        console.warn(
          '[marketing] Falling back to bundled defaults — backend:',
          apiErr.message, '· static:', fileErr.message,
        );
      }
    }
    cachedConfig = raw ? mergeConfig(raw) : defaultConfig;
    if (source !== 'default' && typeof console !== 'undefined') {
      // eslint-disable-next-line no-console
      console.debug(`[marketing] site config loaded from ${source}`);
    }
    inFlight = null;
    return cachedConfig;
  })();
  return inFlight;
}

/**
 * Force the next useSiteConfig() call to refetch.  Useful from the admin
 * editor after Publish so the live preview tab reflects the new content.
 */
export function invalidateSiteConfig() {
  cachedConfig = null;
  inFlight     = null;
}

export default function useSiteConfig() {
  const [config, setConfig] = useState(cachedConfig || defaultConfig);
  const [ready,  setReady]  = useState(!!cachedConfig);

  useEffect(() => {
    let alive = true;
    loadConfig().then((c) => {
      if (!alive) return;
      setConfig(c);
      setReady(true);
    });
    return () => { alive = false; };
  }, []);

  return { config, ready };
}
